import axios from "axios"
import axiosRetry, { isNetworkOrIdempotentRequestError } from 'axios-retry'
import catchUnauthorizedError from '@/utils/catchUnauthorizedError'
import catchForbiddenError from '@/utils/catchForbiddenError'
import catch404 from '@/utils/catch404'
import eventBus from "@/eventBus"
import router from '@/router'
import LogRocket from 'logrocket'
import { apiErrorHandler } from "@/utils/apiErrorHandler"

function simpleNetworkErrorCheck(error) {
  if (!error?.code) {
    return true
  } else {
    return isNetworkOrIdempotentRequestError(error)
  }
}

const httpClient = axios.create({
  baseURL: import.meta.env.VITE_APP_BASE_URL,
  headers: {
    'X-Requested-With': 'XMLHttpRequest',
  },
  withCredentials: true,
})


axiosRetry(httpClient, {
  retries: 1,
  retryCondition: simpleNetworkErrorCheck,
  retryDelay: () => 2000,
})

let recordingURL = null

httpClient.interceptors.request.use((config) => {
  if (['production', 'staging'].includes(import.meta.env.VITE_APP_ENV) && !recordingURL) {
    LogRocket.getSessionURL(function(sessionURL) {
      recordingURL = sessionURL
    })
  }

  const token = localStorage.getItem('token')

  if (config.url.includes('{currentCompanySlug}')) {
    // console.log(config.url, 'before')
    config.url = config.url.replace('{currentCompanySlug}', router.currentRoute.value.params.company || localStorage.getItem('currentCompanySlug'))
    // console.log(config.url, 'after')
  }

  if (token && !config.ignoreGlobalAuthTokenSet) {
    config.headers.Authorization = `Bearer ${token}`
  }

  if (recordingURL) {
    config.headers['X-LogRocket-URL'] = recordingURL
  }

  return config
}, (err) => Promise.reject(err))

httpClient.interceptors.response.use((response) => response, (error) => {
  if (!axios.isAxiosError(error)) {
    return Promise.reject(error)
  }

  if (axios.isCancel(error)) {
    return Promise.reject(error)
  }

  if (error?.response?.status === 401) {
    catchUnauthorizedError()
    return Promise.reject(error)
  }
  if (error?.response?.status === 403) {
    if (error?.response?.data?.errors?.automationValidation) {
      const { automationValidation } = error.response.data.errors
      eventBus.$emit('statusErrorCaptured', {
        ...automationValidation,
        hideValidationSections: Boolean(error?.config?.hideValidationSections),
        recordId: error?.config?.validationRecordId || null,
      })

      return Promise.reject(error)
    }

    catchForbiddenError()
    return Promise.reject(error)
  }
  if (error?.response?.status === 404 && !error.config.ignoreGlobalNotFoundCatch) {
    catch404()
    return Promise.reject(error)
  }
  if (!error?.response?.status || String(error?.response?.status || '')[0] === '5') {
    if (error?.config['axios-retry'].retryCount >= 1) {
      eventBus.$emit('requestErrorCaptured', 504)
      return Promise.reject(error)
    }
  }

  apiErrorHandler(error)

  return Promise.reject(error)
})

export default httpClient
